import React from 'react'
import styled from '@emotion/styled'
import { useTheme } from 'emotion-theming'
import { array, object, string } from 'prop-types'

// Import Components
import { Chevron } from '@jelly/components'
import { RelatedCard } from './RelatedCard'

import { makeImgResizerUrl } from '@jelly/utils'
import { SponsoredLink } from '../SponsoredLink'
import { Link } from '@jelly/ui'

// Styled Components via Emotion
const RelatedContentCardContainer = styled.div`
	border: 1px solid ${({ theme }) => theme.colors.grey01};
	box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.16);
	display: block;
	font-weight: 300;
	height: auto;
	margin-bottom: 20px;

	${({ theme }) => theme.MQ.M} {
		width: 300px;
	}
`
const HeaderWrapper = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.colors.grey01};
	padding: 10px 15px;
`
const Header = styled.div`
	color: ${({ theme }) => theme.colors.gray[600]};
	font-size: 21px;
	${({ theme }) => theme.relatedRail.headerFontFamily};
`
const SponsoredHeader = styled.div`
	align-items: center;
	display: flex;
	height: 55px;
`
const SponsoredLogo = styled.img`
	bottom: 5px;
	display: inline-block;
	height: auto;
	max-height: 30px;
	max-width: 150px;
	padding-left: 8px;
	width: auto;
`
const SeeMore = styled.div`
	color: ${({ theme }) => theme.colors.contentLink};
	font-size: 14px;

	svg {
		padding-bottom: 8px;
		transform: rotate(90deg);
		vertical-align: middle;
	}
`
const MoreRelated = styled(SeeMore)`
	font-size: 15px;
	padding: 10px 15px;
	text-align: right;
`
const LinkWrapper = styled(Link)`
	color: ${({ theme }) => theme.relatedRail.linkColor};
	display: flex;
	align-items: center;
`

RelatedContentCards.propTypes = {
	articles: array,
	bestPicks: array,
	contentType: string,
	experts: array,
	relatedSponsoredArticles: array,
	reviews: array,
	seeMoreLink: string,
	sponsorship: object,
	theme: object,
}

/**
 * This is the Related Content Card components
 * Used to display related content
 * Supports round thumbnail shapes
 */
export function RelatedContentCards(props) {
	const {
		articles,
		bestPicks,
		contentType,
		experts,
		relatedSponsoredArticles,
		reviews,
		seeMoreLink,
		sponsorship,
	} = props

	const {
		relatedRail: { linkColor },
	} = useTheme()

	if (sponsorship) {
		if (relatedSponsoredArticles && relatedSponsoredArticles.length > 0) {
			return (
				<RelatedContentCardContainer>
					<HeaderWrapper>
						<SponsoredHeader>
							More from{' '}
							<SponsoredLink sponsorLink={sponsorship.social.website} sponsorName={sponsorship.name}>
								<SponsoredLogo
									alt={sponsorship.name}
									className="lazyload"
									data-src={makeImgResizerUrl({
										src: sponsorship.thumbnail,
										method: 'h',
										h: '40-png',
									})}
								/>
							</SponsoredLink>
						</SponsoredHeader>
					</HeaderWrapper>
					{relatedSponsoredArticles.map((sponsoredArticle) => (
						<RelatedCard
							header1={sponsoredArticle.title}
							link={sponsoredArticle.canonical}
							key={sponsoredArticle.title}
							thumbnail={makeImgResizerUrl({
								src: sponsoredArticle.thumbnail,
								method: 'r',
								w: 65,
								h: 65,
							})}
						/>
					))}
					{/* Related Component Footer */}
					{seeMoreLink.trim() && (
						<MoreRelated>
							<LinkWrapper href={seeMoreLink}>
								More Related Articles
								<Chevron color={linkColor} height={15} width={10} rotationDeg={90} />
							</LinkWrapper>
						</MoreRelated>
					)}
				</RelatedContentCardContainer>
			)
		}
		return null
	}
	return (
		<RelatedContentCardContainer>
			{/* Related Component Header */}
			<HeaderWrapper>
				<Header>Related {contentType}</Header>
				{seeMoreLink.trim() && (
					<SeeMore>
						<LinkWrapper href={seeMoreLink}>
							See More Related {contentType}
							<Chevron color={linkColor} height={15} width={10} rotationDeg={90} />
						</LinkWrapper>
					</SeeMore>
				)}
			</HeaderWrapper>
			{/* Related Component Cards */}
			{bestPicks &&
				bestPicks.length > 0 &&
				bestPicks.map((bestPick, i) => (
					<RelatedCard
						header1={bestPick.title}
						link={bestPick.canonical}
						key={i}
						sub="Read Review"
						thumbnail={makeImgResizerUrl({ src: bestPick.thumbnail, method: 'r', w: 65, h: 65 })}
					/>
				))}
			{articles &&
				articles.length > 0 &&
				articles
					.slice(0, 7)
					.map((article, i) => (
						<RelatedCard
							header1={article.title}
							contentType={contentType}
							link={article.canonical}
							key={i}
							sponsored={article.sponsored}
							thumbnail={makeImgResizerUrl({ src: article.thumbnail, method: 'rc', w: 65, h: 65 })}
						/>
					))}
			{reviews &&
				reviews.length > 0 &&
				reviews.map((review, i) => {
					const reviewDescription = review.dek.substring(0, 55)
					const description = reviewDescription.substr(
						0,
						Math.min(reviewDescription.length, reviewDescription.lastIndexOf(' '))
					)
					return (
						<RelatedCard
							header1={review.title}
							header2={`${description}...`}
							link={review.canonical}
							key={i}
							thumbnail={makeImgResizerUrl({ src: review.thumbnail, method: 'r', w: 65, h: 65 })}
						/>
					)
				})}
			{experts &&
				experts.length > 0 &&
				experts.map((expert, i) => (
					<RelatedCard
						header1={expert.name}
						header2={expert.jobTitle}
						isProfile={true}
						key={i}
						link={`/advice/member/p/${expert.slug}`}
						sub="See Profile"
						subArrow={true}
						thumbnail={makeImgResizerUrl({ src: expert.imageOriginal, method: 'rc', w: 65, h: 65 })}
					/>
				))}
			{/* Related Component Footer */}
			{seeMoreLink.trim() && (
				<MoreRelated>
					<LinkWrapper href={seeMoreLink}>
						More Related {contentType}
						<Chevron color={linkColor} height={15} width={10} rotationDeg={90} />
					</LinkWrapper>
				</MoreRelated>
			)}
		</RelatedContentCardContainer>
	)
}
